import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Loader from '../../components/Common/Loader';
import { Link, useNavigate } from 'react-router-dom';
import { getURLSearchParams } from '../../helper/helper';
import { saveRespondPreOrderData } from './services/Respond.service';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/store';

export interface RespondModel {
  firstName: string;
  lastName: string;
  classCode: string;
  studentUsername: string;
  email: string;
  orderDecision: string;
  confirm?: boolean;
  confirmShare?: boolean;
}

const RespondSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  classCode: Yup.string().trim().required('Class code is required'),
  studentUsername: Yup.string().trim().required('Student username is required'),
  email: Yup.string().trim().email('Invalid email address').required('Email is required'),
  orderDecision: Yup.string().trim().required('Please select an option'),
  confirm: Yup.boolean().oneOf([true], 'Please confirm'),
  confirmShare: Yup.boolean(),
});

const Respond = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  // State for initial values
  const [initialValues, setInitialValues] = useState<RespondModel>({
    firstName: '',
    lastName: '',
    classCode: '',
    studentUsername: '',
    email: '',
    orderDecision: '',
    confirm: false,
    confirmShare: false,
  });

  useEffect(() => {
    const searchParams = getURLSearchParams(true);
    const classCode = searchParams.get('classCode') || '';
    const userName = searchParams.get('userName') || '';
    setInitialValues((prev) => ({
      ...prev,
      classCode,
      studentUsername: userName,
    }));
  }, []);

  const saveRespondDataForm = async (data: RespondModel) => {
    try {
      await saveRespondPreOrderData(data, dispatch);
    } catch (error) {
      console.error('Error saving response data:', error);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[100vh] w-full font-sofiaSans">
      <div className="flex flex-col items-start w-full max-w-sm bg-white rounded-lg p-8 shadow-none">
        <img src="/new_blue_logo.svg" alt="Build My Story Logo" className="h-12 mb-6" />
        <h1 className="text-3xl font-bold text-secondaryStoryOrangeText text-left mb-2">
          Get Ready for the
          <br />
          Publishing Celebration
        </h1>
        <p className="text-left text-base text-secondaryStoryBlackText mb-6">
          One quick yes from you, and the adventure can begin.
        </p>
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          validationSchema={RespondSchema}
          onSubmit={async (values, { setSubmitting }) => {
            await saveRespondDataForm(values);
            setSubmitting(false);
          }}
        >
          {({ errors, touched, values, handleChange, handleSubmit, setFieldValue, isSubmitting }) => (
            <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit}>
              <div className="flex gap-2 relative required-flag-top-right">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    name="firstName"
                    value={values.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                    className="w-full appearance-none block px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-secondaryStoryBlackText placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackTex"
                  />
                  {touched.firstName && errors.firstName && (
                    <div className="absolute text-storyseconderror text-xs">{errors.firstName}</div>
                  )}
                </div>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    name="lastName"
                    value={values.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    className="w-full appearance-none block px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-secondaryStoryBlackText placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackTex"
                  />
                  {touched.lastName && errors.lastName && (
                    <div className="absolute text-storyseconderror text-xs">{errors.lastName}</div>
                  )}
                </div>
              </div>
              <div className="relative required-flag-top-right">
                <input
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  className="w-full appearance-none block px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-secondaryStoryBlackText placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackTex"
                />
                {touched.email && errors.email && (
                  <div className="absolute text-storyseconderror text-xs">{errors.email}</div>
                )}
              </div>
              <div className="relative required-flag-top-right">
                <input
                  type="text"
                  name="classCode"
                  value={values.classCode}
                  onChange={handleChange}
                  placeholder="Class Code"
                  className="w-full appearance-none block px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-secondaryStoryBlackText placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackTex"
                />
                {touched.classCode && errors.classCode && (
                  <div className="absolute text-storyseconderror text-xs">{errors.classCode}</div>
                )}
              </div>
              <div className="relative required-flag-top-right">
                <input
                  type="text"
                  name="studentUsername"
                  value={values.studentUsername}
                  onChange={handleChange}
                  placeholder="Student Username"
                  className="w-full appearance-none block px-4 py-2 border border-secondaryStoryGrayBorder rounded-lg placeholder-secondaryStoryBlackText placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:border-secondaryStoryAdditionalLightBlueText text-base text-secondaryStoryBlackTex"
                />
                {touched.studentUsername && errors.studentUsername && (
                  <div className="absolute text-storyseconderror text-xs mt">{errors.studentUsername}</div>
                )}
              </div>
              <div className="relative">
                <div className="w-full flex items-start mt-3 pb-3 text-left relative">
                  <input
                    id="confirm"
                    name="confirm"
                    type="checkbox"
                    checked={values.confirm}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 text-secondaryStoryOrangeText border-gray-300 rounded focus:ring-secondaryStoryOrangeText"
                  />
                  <label htmlFor="confirm" className="ml-2 text-base text-secondaryStoryBlackText select-none">
                    <span className="font-bold mr-1">Required:</span>I confirm I am the child’s parent or legal guardian
                    and I give permission for them to use Build My Story. I agree to the{' '}
                    <Link
                      to="https://buildmystory.com/terms-and-conditions/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondaryStoryAdditionalLightBlueText hover:underline"
                    >
                      Terms of Use
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="https://buildmystory.com/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-secondaryStoryAdditionalLightBlueText hover:underline"
                    >
                      Privacy Policy
                    </Link>
                  </label>

                  {touched.confirm && errors.confirm && (
                    <div className="absolute bottom-0 text-storyseconderror text-xs">{errors.confirm}</div>
                  )}
                </div>
                <div className="w-full flex items-start mt-1 text-left">
                  <input
                    id="confirmShare"
                    name="confirmShare"
                    type="checkbox"
                    checked={values.confirmShare}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 text-secondaryStoryOrangeText border-gray-300 rounded focus:ring-secondaryStoryOrangeText"
                  />
                  <label htmlFor="confirmShare" className="ml-2 text-base text-secondaryStoryBlackText select-none">
                    <span className="font-bold mr-1">Optional:</span>Yes, you can share my child’s book to help inspire
                    other kids, including on your site, social, or promos.
                  </label>
                </div>
              </div>
              <div className="mt-2 relative">
                <div className="font-bold text-secondaryStoryOrangeText text-left mb-2">Have you decided to order?</div>
                <div className="flex flex-col gap-3 text-left">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="orderDecision"
                      value="yes"
                      checked={values.orderDecision === 'yes'}
                      onChange={() => setFieldValue('orderDecision', 'yes')}
                      className="accent-storyorange"
                    />
                    <span className="text-base text-secondaryStoryBlackText">
                      Yes, I want to pre-order my child's book
                    </span>
                  </label>
                  <label className="flex items-baseline gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="orderDecision"
                      value="no"
                      checked={values.orderDecision === 'no'}
                      onChange={() => setFieldValue('orderDecision', 'no')}
                      className="accent-storyorange"
                    />
                    <span className="text-base text-secondaryStoryBlackText">
                      No, I'm not ordering yet, but I've received this info
                    </span>
                  </label>
                </div>
                {touched.orderDecision && errors.orderDecision && (
                  <div className="absolute text-storyseconderror text-xs mt-0.5">{errors.orderDecision}</div>
                )}
              </div>
              <button
                type="submit"
                className="w-full mt-1 py-2 mb-2 flex items-center justify-center rounded-lg bg-secondaryStoryOrangeBackground text-whiteText font-bold text-base shadow-md hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-secondaryStoryAdditionalLightBlueText focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                Continue
                <Loader loader={isSubmitting} />
              </button>
            </form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default Respond;
